/* Divine Designs: intentional set purchases, with Shopify as the final price/stock authority. */
(() => {
  'use strict';
  if (customElements.get('divine-offer-builder')) return;
  let pendingProductSwap = null;
  class DivineOfferBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.catalog = JSON.parse(this.querySelector('[data-offer-data]').textContent);
      this.primaryData = this.catalog.products.find((product) => String(product.id) === String(this.catalog.primaryId));
      this.count = 1;
      this.paidCount = 1;
      this.productInfo = this.closest('product-info') || document;
      this.button = this.querySelector('[data-add-set]');
      this.primarySelect = this.querySelector('[data-primary-product]');
      this.rows = [...this.querySelectorAll('[data-piece]')].map((element) => ({ element, productSelect: element.querySelector('[data-product]'), variantSelect: element.querySelector('[data-variant]') }));
      this.tiers = [...this.querySelectorAll('[data-tier]')];
      const options = { signal: this.controller.signal };
      this.addEventListener('change', (event) => {
        if (this.busy || this.swapping) return;
        if (event.target === this.primarySelect) { this.switchPrimary(); return; }
        if (event.target.matches('[data-tier]')) { this.choosePaid(Number(event.target.dataset.paid)); return; }
        const row = this.rows.find((entry) => entry.element.contains(event.target));
        if (row && this.rows.indexOf(row) + 1 >= this.plan().paid && !this.plan().paidReady) return;
        if (row && event.target === row.productSelect) this.updateProduct(row);
        if (row) this.updateVariant(row);
        this.refresh();
      }, options);
      this.productInfo.addEventListener('change', (event) => { if (!this.contains(event.target)) this.refresh(); }, options);
      this.productInfo.addEventListener('input', (event) => { if (!this.contains(event.target)) this.refresh(); }, options);
      this.querySelector('[data-reset-quantity]').addEventListener('click', () => {
        const { quantityInput } = this.getPrimary();
        if (quantityInput) { quantityInput.value = '1'; quantityInput.dispatchEvent(new Event('change', { bubbles: true })); }
        this.refresh();
      }, options);
      this.button.addEventListener('click', () => this.addSet(), options);
      this.querySelector('[data-match-paid]').addEventListener('click', () => this.matchPaid(), options);
      this.querySelector('[data-match-gifts]').addEventListener('click', () => this.matchGifts(), options);
      this.populateProductChoices(this.primarySelect, String(this.primaryData.id), false);
      this.rows.forEach((row) => this.populateProducts(row));
      this.restoreProductSwap();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.sectionId) return;
          const variant = this.primaryData.variants.find((entry) => String(entry.id) === String(data.variant?.id));
          if (variant) Object.assign(variant, { available: data.variant.available, price: data.variant.price });
          this.refresh();
        });
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.optionValueSelectionChange) {
        this.unsubscribeOptions = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          if (this.productInfo.contains(data.event.target)) this.refresh();
        });
      }
      const nativeButton = this.getPrimary().form?.querySelector('[name="add"]');
      if (nativeButton && typeof MutationObserver !== 'undefined') {
        this.nativeButtonObserver = new MutationObserver(() => this.refresh());
        this.nativeButtonObserver.observe(nativeButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
      }
      this.classList.add('dd-offer--ready');
      this.refresh();
      // Move the same element on mobile so its choices and keyboard order stay intact.
      this.originalPlace = document.createComment('Original bundle position');
      this.before(this.originalPlace);
      this.mobilePlacement = window.matchMedia('(max-width: 749px)');
      this.mobilePlacement.addEventListener('change', () => this.placeOffer(), options);
      this.placeOffer();
    }
    disconnectedCallback() { if (this.relocating) return; this.controller?.abort(); this.controller = null; this.primarySwapController?.abort(); this.primarySwapController = null; this.unsubscribe?.(); this.unsubscribe = null; this.unsubscribeOptions?.(); this.unsubscribeOptions = null; this.nativeButtonObserver?.disconnect(); this.originalPlace?.remove(); this.originalPlace = null; this.productInfo?.classList?.remove('dd-offer-set-active'); }
    placeOffer() {
      if (!this.originalPlace?.parentNode || !this.productInfo) return;
      const purchase = this.productInfo.querySelector('product-form[data-dd-primary-purchase]')?.parentElement;
      const mobile = this.mobilePlacement?.matches && purchase?.parentElement === this.originalPlace.parentElement;
      const parent = this.originalPlace.parentNode;
      const target = mobile ? purchase : this.originalPlace.nextSibling;
      if (target === this || (mobile && this.nextSibling === purchase)) return;
      this.relocating = true;
      try { parent.insertBefore(this, target); }
      finally { this.relocating = false; }
    }
    money(cents) { return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100); }
    getPrimary() {
      const form = document.getElementById(this.dataset.formId);
      const variant = this.primaryData?.variants.find((entry) => String(entry.id) === String(form?.querySelector('[name="id"]')?.value));
      const quantityInput = form?.querySelector('[name="quantity"]') || this.productInfo.querySelector('[name="quantity"][form="' + this.dataset.formId + '"]');
      return { form, variant, quantityInput, quantity: Number(quantityInput?.value || 1) };
    }
    getRow(row) {
      const product = this.catalog.products.find((entry) => String(entry.id) === row.productSelect.value);
      return { product, variant: product?.variants.find((entry) => String(entry.id) === row.variantSelect.value) };
    }
    updateProduct(row) {
      const { product } = this.getRow(row);
      row.variantSelect.replaceChildren();
      if (!product) return;
      const current = this.getPrimary().variant;
      let firstAvailable = product.variants.find((variant) => variant.available);
      if (product.id === this.primaryData.id && current?.available) firstAvailable = current;
      for (const variant of product.variants) {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = (variant.title === 'Default Title' ? 'One option' : variant.title) + ' — ' + this.money(variant.price) + (variant.available ? '' : ' · Sold out');
        option.disabled = !variant.available;
        option.selected = variant.id === firstAvailable?.id;
        row.variantSelect.append(option);
      }
    }
    updateVariant(row) {
      const { product, variant } = this.getRow(row);
      row.element.querySelector('[data-piece-price]').textContent = variant ? this.money(variant.price) : '';
      row.variantSelect.hidden = !product;
      const image = row.element.querySelector('[data-piece-image]');
      row.element.querySelector('[data-piece-photo]').hidden = !variant?.image;
      if (variant?.image) { image.src = variant.image; image.alt = product.title + ' — ' + variant.title; }
      const link = row.element.querySelector('[data-piece-link]');
      link.hidden = !variant;
      if (variant) link.href = product.url + '?variant=' + variant.id;
    }
    quantityError(variant, quantity, title) {
      const min = Number(variant.min) || 1, step = Number(variant.increment) || 1;
      if (!Number.isInteger(quantity) || quantity < min || (quantity - min) % step !== 0) return `${title}: this option requires at least ${min}, in increments of ${step}. Add this piece from its product page.`;
      if (variant.max != null && quantity > Number(variant.max)) return `${title}: the maximum quantity is ${variant.max}.`;
      return '';
    }
    rule() { return { b2g1: { paid: 2, free: 1 }, b2g2: { paid: 2, free: 2 }, b3g2: { paid: 3, free: 2 } }[this.dataset.offerMode] || null; }
    plan() {
      const rule = this.rule();
      const free = rule ? Math.floor(this.count / (rule.paid + rule.free)) * rule.free : 0;
      const paid = this.count - free;
      const primary = this.getPrimary();
      const paidReady = !!primary.variant?.available && primary.quantity === 1 && !this.productInfo.pendingRequestUrl && this.rows.slice(0, paid - 1).every(row => this.getRow(row).variant?.available);
      return { paid, free, total: this.count, paidReady };
    }
    populateProductChoices(select, selected, placeholder = true) {
      select.replaceChildren();
      if (placeholder) {
        const option = document.createElement('option');option.value = '';option.textContent = 'Choose qualifying jewelry';select.append(option);
      }
      const groups = new Map();
      for (const product of this.catalog.products) {
        if (!product.eligible) continue;
        const name = (product.handle || product.title).toLowerCase();
        const category = /necklace/.test(name) ? 'Necklaces' : /bracelet/.test(name) ? 'Bracelets' : /earring/.test(name) ? 'Earrings' : /ring/.test(name) ? 'Rings' : 'Jewelry';
        if (!groups.has(category)) { const group = document.createElement('optgroup');group.label = category;groups.set(category, group);select.append(group); }
        const option = document.createElement('option');option.value = product.id;
        option.textContent = product.title + (product.variants.some(v => v.available) ? '' : ' · Sold out');
        option.disabled = !product.variants.some(v => v.available) || !!product.requiresSellingPlan;
        groups.get(category).append(option);
      }
      select.value = [...select.options].some(option => option.value === String(selected)) ? String(selected) : '';
    }
    populateProducts(row, gift = false) {
      const selected = row.productSelect.value;
      this.populateProductChoices(row.productSelect, selected);
      row.element.dataset.role = gift ? 'gift' : 'paid';
      if (row.productSelect.value !== selected) this.updateProduct(row);
    }
    restoreProductSwap() {
      const state = pendingProductSwap;
      if (!state || state.expires < Date.now() || String(state.productId) !== String(this.primaryData.id)) return;
      pendingProductSwap = null;
      if (!this.tiers.some(tier => Number(tier.dataset.paid) === state.paidCount && Number(tier.value) === state.count)) return;
      this.paidCount = state.paidCount;
      this.count = state.count;
      state.rows.forEach((choice, index) => {
        const row = this.rows[index];
        const product = this.catalog.products.find(entry => String(entry.id) === String(choice.productId));
        const variant = product?.variants.find(entry => String(entry.id) === String(choice.variantId));
        if (row && product?.eligible && variant?.available) this.setRow(row, { product, variant });
      });
    }
    async switchPrimary() {
      const product = this.catalog.products.find(entry => String(entry.id) === this.primarySelect.value);
      if (!product || !product.eligible || product.requiresSellingPlan || !product.variants.some(variant => variant.available)) {
        this.primarySelect.value = String(this.primaryData.id);
        this.error('Choose an available product for your first piece.');
        return;
      }
      if (String(product.id) === String(this.primaryData.id)) return;
      if (this.productInfo.pendingRequestUrl) {
        this.primarySelect.value = String(this.primaryData.id);
        this.error('Wait for the selected finish to load, then choose a different first piece.');
        return;
      }
      const destination = new URL(product.url, location.href);
      if (destination.origin !== location.origin) {
        this.primarySelect.value = String(this.primaryData.id);
        this.error('That product could not be loaded. Please choose another.');
        return;
      }
      const previewTheme = new URLSearchParams(location.search).get('preview_theme_id');
      if (previewTheme) destination.searchParams.set('preview_theme_id', previewTheme);
      // The 13 mm Gold and White finish is our most popular starting choice.
      if (product.handle === 'small-pendant-necklace') {
        const popular = product.variants.find(variant => variant.available && variant.title === 'Gold and White');
        if (popular) destination.searchParams.set('variant', popular.id);
      }
      this.swapping = true;
      this.error();
      this.setAttribute('aria-busy', 'true');
      this.refresh();
      this.primarySwapController?.abort();
      this.primarySwapController = new AbortController();
      const signal = this.primarySwapController.signal;
      try {
        await customElements.whenDefined('product-info');
        if (signal.aborted) return;
        const response = await fetch(destination, { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('That product could not be loaded. Please try again.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector("product-info[id^='MainProduct']");
        const nextBuilder = next?.querySelector('divine-offer-builder');
        const nextData = nextBuilder?.querySelector('[data-offer-data]');
        if (!next || !html.querySelector('main') || !html.querySelector('head title') ||
            !nextData || String(JSON.parse(nextData.textContent).primaryId) !== String(product.id) ||
            nextBuilder.dataset.offerMode !== this.dataset.offerMode) {
          throw new Error('This product cannot be used in the set right now. Please choose another.');
        }
        if (signal.aborted || !this.isConnected) return;
        pendingProductSwap = {
          productId: product.id, count: this.count, paidCount: this.paidCount,
          rows: this.rows.map(row => {
            const choice = this.getRow(row);
            return { productId: choice.product?.id, variantId: choice.variant?.id };
          }),
          expires: Date.now() + 10000
        };
        const position = this.getBoundingClientRect().top;
        this.productInfo.handleSwapProduct(destination.pathname, true)(html);
        if (previewTheme) {
          const updated = new URL(location.href);
          updated.searchParams.set('preview_theme_id', previewTheme);
          history.replaceState(history.state, '', updated.pathname + updated.search + updated.hash);
        }
        const active = document.querySelector("product-info[id^='MainProduct'] divine-offer-builder");
        if (active && active !== this) {
          scrollBy(0, active.getBoundingClientRect().top - position);
          active.querySelector('[data-primary-product]')?.focus({ preventScroll: true });
        }
      } catch (error) {
        pendingProductSwap = null;
        if (error.name !== 'AbortError') {
          this.primarySelect.value = String(this.primaryData.id);
          this.error(error.message || 'That product could not be loaded. Please try again.');
        }
      } finally {
        if (!signal.aborted && this.isConnected) {
          this.swapping = false;
          this.removeAttribute('aria-busy');
          this.refresh();
        }
      }
    }
    setRow(row, choice) {
      row.productSelect.value = choice?.product ? String(choice.product.id) : '';
      this.updateProduct(row);
      if (choice?.variant) row.variantSelect.value = String(choice.variant.id);
      this.updateVariant(row);
    }
    choosePaid(paid) {
      if (this.busy || this.swapping || !Number.isInteger(paid) || paid < 1 || paid > 4) return;
      const old = this.plan(), paidRows = this.rows.slice(0, old.paid - 1).map(row => this.getRow(row)), giftRows = this.rows.slice(old.paid - 1, this.count - 1).map(row => this.getRow(row));
      const rule = this.rule(), free = rule ? Math.floor(paid / rule.paid) * rule.free : 0;
      this.paidCount = paid;this.count = paid + free;
      const choices = Array.from({length: paid - 1}, (_, i) => paidRows[i] || null).concat(Array.from({length: free}, (_, i) => giftRows[i] || null));
      this.rows.forEach((row, i) => this.setRow(row, choices[i]));
      if (paid > 1) {
        const quantityInput = this.getPrimary().quantityInput;
        if (quantityInput && Number(quantityInput.value) !== 1) {
          quantityInput.value = '1';
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.error();this.refresh();
    }
    matchPaid() {
      if (this.busy || this.swapping) return;
      const variant = this.getPrimary().variant;if (!variant?.available) return;
      this.rows.slice(0, this.plan().paid - 1).forEach(row => this.setRow(row, {product:this.primaryData,variant}));this.refresh();
    }
    matchGifts() {
      const plan = this.plan();if (this.busy || this.swapping || !plan.paidReady) return;
      const variant = this.getPrimary().variant;
      this.rows.slice(plan.paid - 1, this.count - 1).forEach(row => this.setRow(row, {product:this.primaryData,variant}));this.refresh();
    }
    selection() {
      if (this.swapping) return { error: 'Updating your first piece…' };
      if (this.count === 1) return { error: 'Use the main Add to cart button for one piece.', single: true };
      if (!Number.isInteger(this.count) || this.count < 2 || this.count > 8) return {error:'Choose a valid set.'};
      const { form, variant, quantity } = this.getPrimary();
      const nativeButton = form?.querySelector('[name="add"]');
      if (this.productInfo.pendingRequestUrl || nativeButton?.disabled || nativeButton?.getAttribute('aria-disabled') === 'true') return { error: 'Wait for your selected finish to finish loading, or choose an available option below.' };
      if (!form || !variant?.available) return { error: 'Choose an available finish below to build your set.' };
      if (quantity !== 1) return { error: 'Use one of each selected piece. Your set includes the paid pieces and gifts chosen below.', quantity: true };
      if (form.querySelector('[name="selling_plan"]')?.value || this.primaryData.requiresSellingPlan) return { error: 'Please use the main purchase button for subscription purchases.' };
      const plan = this.plan(), items = [{ id: variant.id, quantity: 1, variant, product: this.primaryData }];
      for (const [index, row] of this.rows.slice(0, this.count - 1).entries()) {
        const choice = this.getRow(row);
        if (!choice.product) return { error: index + 1 < plan.paid ? `Choose your first ${plan.paid} pieces.` : `Choose ${plan.free === 1 ? 'the final piece' : 'your final pieces'} to complete your set.`, incomplete: true, step: index + 1 < plan.paid ? 'paid' : 'gifts' };
        if (!choice.variant?.available) return { error: 'One of your selected options is unavailable. Please choose another.' };
        if (!choice.product.eligible) return { error: 'Choose qualifying jewelry for this offer.' };
        if (choice.product.requiresSellingPlan) return { error: 'Subscription-only pieces must be added from their product pages.' };
        items.push({ id: choice.variant.id, quantity: 1, ...choice });
      }
      for (const item of items) {
        if (item.product.variantCount > 250) return { error: 'Please add this piece from its product page to see all options.' };
        const error = this.quantityError(item.variant, 1, item.product.title);if (error) return { error };
      }
      const subtotal = items.reduce((total, item) => total + item.variant.price, 0);
      const promotion = plan.free > 0 && items.every(item => item.product.eligible);
      const freeCount = promotion ? plan.free : 0;
      const freeIndexes = new Set(items.map((item,index)=>({index,price:item.variant.price})).sort((a,b)=>a.price-b.price||b.index-a.index).slice(0,freeCount).map(item=>item.index));
      const lines = items.map((item,index)=>({free:freeIndexes.has(index),subtotal:item.variant.price,savings:freeIndexes.has(index)?item.variant.price:0,total:freeIndexes.has(index)?0:item.variant.price}));
      const savings = lines.reduce((total,line)=>total+line.savings,0);
      return {items,subtotal,total:subtotal-savings,savings,promotion,freeCount,paidCount:items.length-freeCount,lines};
    }
    paintPrice(element, price, free) {
      if (!element) return;
      element.textContent = '';
      if (free) { const before = document.createElement('s');before.textContent = this.money(price);const badge = document.createElement('b');badge.textContent = 'FREE';element.append(before,badge); }
      else element.textContent = price == null ? '' : this.money(price);
    }
    refresh() {
      if (!this.rows) return;
      const primary = this.getPrimary(), plan = this.plan(), isSet = this.count > 1;
      this.productInfo.classList?.toggle('dd-offer-set-active',isSet);
      this.querySelector('[data-builder]').hidden = !isSet;this.querySelector('[data-single-note]').hidden = isSet;
      this.tiers.forEach(tier=>{tier.disabled=!!this.busy||!!this.swapping;tier.checked=Number(tier.value)===this.count;});
      this.primarySelect.disabled=!!this.busy||!!this.swapping;
      this.querySelector('[data-paid-heading]').textContent=`Choose your first ${plan.paid} pieces`;
      this.querySelector('[data-gift-panel]').hidden = !isSet || !plan.free;
      this.querySelector('[data-gift-heading]').textContent = plan.free === 1 ? 'Choose your third piece' : `Choose ${plan.free} more pieces`;
      this.querySelector('[data-gift-status]').textContent = plan.paidReady ? 'Choose any qualifying piece. Your bag shows the offer savings.' : `Choose your first ${plan.paid} pieces above.`;
      this.querySelector('[data-gift-step]').disabled = !plan.paidReady || !!this.busy || !!this.swapping;
      this.querySelector('[data-gift-rule]').textContent = this.dataset.previewOffer === 'true' ? 'Review estimate only. Shopify must enable the discount before checkout savings apply.' : `For each set, the ${plan.free === 1 ? 'free item is the least expensive qualifying piece' : `${plan.free} free items are the least expensive qualifying pieces`}. Your bag confirms actual savings.`;
      this.querySelector('[data-match-paid]').disabled = !!this.busy || !!this.swapping || !primary.variant?.available;
      this.querySelector('[data-match-gifts]').disabled = !!this.busy || !!this.swapping || !plan.paidReady;
      this.rows.forEach((row,index) => {
        const role = index + 1 >= plan.paid ? 'gift' : 'paid';
        if (row.element.dataset.role !== role) this.populateProducts(row, role === 'gift');
      });
      const selection = this.selection();
      this.rows.forEach((row,index)=>{
        const active = isSet && index < this.count - 1, gift = index + 1 >= plan.paid;
        const target = this.querySelector(active ? gift ? '[data-gift-rows]' : '[data-paid-rows]' : '[data-spare-rows]');
        if (target && row.element.parentElement !== target) target.append(row.element);
        row.element.hidden = !active;row.element.dataset.free=String(!!(!selection.error&&selection.lines?.[index+1]?.free));
        row.productSelect.disabled = !active || !!this.busy || !!this.swapping || gift&&!plan.paidReady;
        row.variantSelect.disabled = row.productSelect.disabled || !this.getRow(row).product;
        row.element.querySelector('[data-row-label]').textContent=`Piece ${index+2} · Qualifying jewelry`;
        row.element.querySelector('[data-row-number]').textContent=String(index+2);
        this.updateVariant(row);
        this.paintPrice(row.element.querySelector('[data-piece-price]'),this.getRow(row).variant?.price,!selection.error&&selection.lines?.[index+1]?.free);
      });
      this.paintPrice(this.querySelector('[data-primary-price]'),primary.variant?.price,!selection.error&&selection.lines?.[0]?.free);
      this.querySelector('.dd-offer__primary').dataset.free=String(!!(!selection.error&&selection.lines?.[0]?.free));
      this.querySelector('[data-primary-title]').textContent=primary.variant?.title==='Default Title'?'Your selected piece':primary.variant?.title||'Choose a finish above';
      const image=this.querySelector('.dd-offer__primary img');if(image&&primary.variant?.image){image.src=primary.variant.image;image.removeAttribute('srcset');}
      this.button.disabled=!!selection.error||!!this.busy||!!this.swapping;
      this.querySelector('[data-button-label]').textContent=this.swapping?'Updating your first piece…':this.busy?'Adding your set…':selection.incomplete?selection.step==='gifts'?'Choose your remaining pieces':'Choose your pieces':this.dataset.previewOffer==='true'?'Preview my set':this.dataset.buttonLabel||'Add to cart';
      this.querySelector('[data-guidance]').textContent=selection.error||`${this.count} pieces selected${selection.freeCount ? ' · '+selection.paidCount+' paid · '+selection.freeCount+' free' : ''}.`;
      this.querySelector('[data-quantity-fix]').hidden=!selection.quantity;this.querySelector('[data-summary]').hidden=!!selection.error;
      const reallocated=!selection.error&&selection.lines.some((line,i)=>i<plan.paid&&line.free);
      this.querySelector('[data-allocation]').hidden=!reallocated;
      this.querySelector('[data-allocation]').textContent=reallocated?'An earlier piece is marked FREE under this offer. Review the actual discount in your bag.':'';
      if(!selection.error){
        this.querySelector('[data-total-label]').textContent=this.dataset.previewOffer==='true'?'Review estimate':selection.promotion?'Estimated set total':'Set total';
        this.querySelector('[data-total]').textContent=this.money(selection.total);this.querySelector('[data-subtotal]').textContent=this.money(selection.subtotal);this.querySelector('[data-subtotal]').hidden=!selection.promotion;
        this.querySelector('[data-saving]').hidden=!selection.promotion;this.querySelector('[data-saving]').textContent=`${selection.paidCount} paid · ${selection.freeCount} free · Estimated savings ${this.money(selection.savings)}`;
        this.querySelector('[data-terms]').textContent=this.dataset.previewOffer==='true'?'Example savings only. Shopify must enable the discount before checkout savings apply.':selection.promotion?'For each set, the discount applies to the least expensive qualifying pieces. Your bag confirms actual savings, including pieces already in it. Shipping and tax calculated at checkout.':'Eligible discounts appear in your bag. Shipping and tax calculated at checkout.';
      }
      if(typeof CustomEvent!=='undefined')this.dispatchEvent(new CustomEvent('divine:offer-change',{bubbles:true}));
    }
    error(message) {
      const element = this.querySelector('[data-error]');
      element.textContent = message || ''; element.hidden = !message;
      this.querySelector('[data-error-cart]').hidden = !message;
    }
    requestItems(selection) {
      const properties = {};
      for (const [key, value] of new FormData(this.getPrimary().form)) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
        if (match && typeof value !== 'string' && value.size) throw new Error('Please add personalized uploads using the main Add to cart button.');
      }
      const merged = new Map();
      selection.items.forEach((item, index) => {
        const itemProperties = index === 0 ? properties : {};
        const key = String(item.id) + JSON.stringify(itemProperties);
        if (merged.has(key)) merged.get(key).quantity += 1;
        else merged.set(key, { id: item.id, quantity: 1, ...(Object.keys(itemProperties).length ? { properties: itemProperties } : {}) });
      });
      for (const item of merged.values()) {
        const source = selection.items.find((entry) => entry.id === item.id);
        const error = this.quantityError(source.variant, item.quantity, source.product.title);
        if (error) throw new Error(error);
      }
      return [...merged.values()];
    }
    async addSet() {
      if (this.busy) return;
      const selection = this.selection();
      this.error();
      if (selection.error) { this.error(selection.error); return; }
      const { form } = this.getPrimary();
      if (form.reportValidity && !form.reportValidity()) return;
      if (this.dataset.previewOffer === 'true') {
        const result = this.querySelector('[data-review-result]');
        result.textContent = `${selection.paidCount} paid pieces + ${selection.freeCount} free. Example savings ${this.money(selection.savings)}; example set total ${this.money(selection.total)}. Review preview only: no products were added to your bag.`;
        result.hidden = false;
        return;
      }
      let items;
      try { items = this.requestItems(selection); } catch (error) { this.error(error.message); return; }
      this.busy = true; this.refresh(); this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-button-label]').textContent = 'Adding your set…';
      this.querySelector('.dd-offer__spinner').hidden = false;
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const cartUrl = this.dataset.cartUrl || '/cart';
      const sections = cart?.getSectionsToRender().map((section) => section.id) || [];
      let added = false;
      try {
        const cartResponse = await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('We could not check your cart. Please try again.');
        const currentCart = await cartResponse.json();
        for (const item of items) {
          const source = selection.items.find((entry) => entry.id === item.id);
          const existing = currentCart.items.filter((line) => String(line.variant_id) === String(item.id)).reduce((sum, line) => sum + line.quantity, 0);
          const chosen = items.filter((line) => String(line.id) === String(item.id)).reduce((sum, line) => sum + line.quantity, 0);
          if (source.variant.max != null && existing + chosen > Number(source.variant.max)) throw new Error(`${source.product.title}: the maximum is ${source.variant.max}, including pieces already in your cart.`);
          if (source.variant.inventoryManaged && source.variant.inventoryPolicy === 'deny' && Number.isFinite(source.variant.inventoryQuantity) && existing + chosen > source.variant.inventoryQuantity) throw new Error(`${source.product.title}: not enough stock for this set and the pieces already in your cart. Choose another option.`);
        }
        // A native variant change can happen while the read-only stock preflight is pending.
        // Recheck immediately before the sole cart mutation; never add a stale hidden ID.
        const confirmedSelection = this.selection();
        if (confirmedSelection.error) throw new Error(confirmedSelection.error);
        if (confirmedSelection.items.map((item) => item.id).join(',') !== selection.items.map((item) => item.id).join(',')) throw new Error('Your selection changed. Review your pieces and add the set again.');
        cart?.setActiveElement(this.button);
        const response = await fetch(this.dataset.addUrl || '/cart/add', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items, ...(sections.length ? { sections, sections_url: window.location.pathname } : {}) }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error((typeof result.description === 'string' && result.description) || 'One of these pieces could not be added. Check availability and your cart before trying again.');
        added = true;
        if (this.dataset.offerMode !== 'none' && window.DivineOfferCartCheck) {
          const eligibleProductIds = this.catalog.products.filter(product => product.eligible).map(product => product.id);
          await window.DivineOfferCartCheck.verify({ mode: this.dataset.offerMode, eligibleProductIds, giftProductIds: eligibleProductIds });
        }
        this.querySelector('[data-status]').textContent = 'Your set was added. Your cart shows any eligible discounts.';
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'divine-offer-builder', productVariantId: items[0].id, cartData: result })).catch(() => {});
        if (!cart || !result.sections || sections.some((id) => !result.sections[id])) { window.location.assign(cartUrl); return; }
        const firstItem = result.items?.[0] || result;
        cart.classList.remove('is-empty');
        cart.renderContents({ ...result, id: firstItem.id, key: firstItem.key });
        window.DivineOfferCartCheck?.paint();
      } catch (error) {
        if (added) window.location.assign(cartUrl);
        else this.error(error.name === 'TypeError' || error.name === 'SyntaxError' ? 'We could not confirm your set was added. Please check your cart before trying again.' : error.message || 'We could not add your set. Please check your cart before trying again.');
      } finally {
        this.busy = false; this.removeAttribute('aria-busy');
        this.querySelector('[data-button-label]').textContent = this.dataset.buttonLabel || 'Add to cart';
        this.querySelector('.dd-offer__spinner').hidden = true;
        this.refresh();
      }
    }
  }
  customElements.define('divine-offer-builder', DivineOfferBuilder);
})();
